import { afterNextRender, DestroyRef, Directive, ElementRef, inject, output } from '@angular/core';
import { uiPx } from 'ra-ui';
import { RailPopoverService } from './rail-popover.service';
import { RAIL_MENU_HOST } from './rail-menu-host';

/**
 * Barra fixa no pé do editor (a seleção em lote). Ela fica por cima da folha: um menu que
 * desce até ela some atrás, então vale como chão.
 */
const FLOOR_SELECTORS = '.selection-actions';

/** Gestos que rolam a página; a rolagem que vem logo depois de um deles é do usuário. */
const GESTURE_EVENTS = ['wheel', 'touchmove', 'keydown', 'pointerdown'] as const;
/** Janela entre o gesto e a rolagem (a inércia da roda segue mandando `wheel`). */
const GESTURE_WINDOW_MS = 600;

/**
 * Posiciona um menu que abre a partir do rail flutuante do card (o ⋯, o tipo do texto,
 * a cor, o estilo...). O SCSS de cada menu define a posição padrão: à esquerda do chip,
 * alinhado pelo topo. Depois de renderizar, a diretiva mede o menu e corrige:
 *
 * - **Direita quando couber.** O rail mora na margem direita da folha: abrir para a
 *   esquerda cobre o próprio card. Com espaço até a borda que corta o menu (o painel de
 *   itens rola; em tela estreita ou com a prévia do PDF aberta não cabe), abre para fora.
 * - **Sobe o necessário.** Perto do pé do painel o menu era cortado ou sumia atrás do
 *   composer fixo. Ele sobe só o que falta para ficar inteiro acima deles, sem passar do
 *   topo visível.
 *
 * O menu é criado a cada abertura (`@if`), então a medida vale para a posição de agora.
 * Rolar a folha fecha o menu (`raRailMenuClose`): ancorado no card, ele rolaria junto e
 * atravessaria o composer fixo no pé, que fica por cima da folha. Só conta a rolagem do
 * USUÁRIO num ancestral do menu: a prévia do PDF redesenha e devolve a própria rolagem a
 * cada save, e um card que encolhe (o − do tamanho) pode empurrar a rolagem do painel;
 * nenhuma das duas é o usuário saindo dali, e fechavam o menu no meio do ajuste.
 * Esc também fecha (só o menu: o editor em foco não sai da edição; ver watchMenuDismiss).
 * Abrir um menu fecha o que estiver aberto em qualquer card (ver RailPopoverService).
 */
@Directive({ selector: '[raRailMenu]' })
export class RaRailMenuDirective {
    /** A folha rolou (ou Esc): quem abriu o menu deve fechá-lo. */
    public raRailMenuClose = output<void>();

    private _el = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

    private _popovers = inject(RailPopoverService);
    private _host = inject(RAIL_MENU_HOST, { optional: true });
    private _release: (() => void) | null = null;
    private _unwatch: (() => void) | null = null;

    constructor() {
        afterNextRender(() => {
            this._place();
            this._unwatch = watchMenuDismiss(this._el, () => this.raRailMenuClose.emit());
            this._release = this._popovers.open(() => this.raRailMenuClose.emit());
            this._host?.railMenusOpen.update(open => open + 1);
        });
        inject(DestroyRef).onDestroy(() => {
            this._unwatch?.();
            if (this._release) {
                this._release();
                this._host?.railMenusOpen.update(open => Math.max(0, open - 1));
            }
        });
    }

    private _place(): void {
        const menu = this._el;
        const anchor = menu.parentElement;
        if (!anchor) return;

        // Vão entre o chip e o menu (0.75rem no SCSS) e folga até as bordas.
        const gap = uiPx(12);
        const margin = uiPx(8);

        const anchorRect = anchor.getBoundingClientRect();
        const menuRect = menu.getBoundingClientRect();
        const area = visibleArea(anchor);

        const opensRight = anchorRect.right + gap + menuRect.width + margin <= area.right;
        if (opensRight) {
            menu.style.right = 'auto';
            menu.style.left = 'calc(100% + 0.75rem)';
        }

        // Faixa horizontal que o menu vai ocupar, para saber quais barras ficam embaixo dele.
        const left = opensRight ? anchorRect.right + gap : anchorRect.left - gap - menuRect.width;
        const right = left + menuRect.width;
        const bottom = Math.min(area.bottom, floorTop(left, right));

        const overflowBottom = anchorRect.top + menuRect.height - (bottom - margin);
        if (overflowBottom > 0) {
            const maxLift = anchorRect.top - (area.top + margin);
            const lift = Math.max(0, Math.min(overflowBottom, maxLift));
            menu.style.top = `${-lift}px`;
        }
    }
}

/** Retângulo visível: a janela recortada por todo ancestral com overflow. */
export function visibleArea(element: HTMLElement): { top: number; right: number; bottom: number } {
    let top = 0;
    let right = window.innerWidth;
    let bottom = window.innerHeight;

    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
        const style = getComputedStyle(parent);
        if (style.overflowX === 'visible' && style.overflowY === 'visible') continue;

        const rect = parent.getBoundingClientRect();
        top = Math.max(top, rect.top);
        right = Math.min(right, rect.right);
        bottom = Math.min(bottom, rect.bottom);
    }

    return { top, right, bottom };
}

/** Topo da barra fixa mais alta que cruza a faixa [left, right] (ou infinito). */
export function floorTop(left: number, right: number): number {
    let top = Number.POSITIVE_INFINITY;

    for (const floor of Array.from(document.querySelectorAll<HTMLElement>(FLOOR_SELECTORS))) {
        const rect = floor.getBoundingClientRect();
        if (rect.height === 0 || rect.right <= left || rect.left >= right) continue;
        top = Math.min(top, rect.top);
    }

    return top;
}

/**
 * Fecha um menu flutuante do rail (ou da barra do topo) quando:
 * - o USUÁRIO rola um ancestral do menu (ver RaRailMenuDirective): rolagem de layout, da
 *   prévia ou de dentro do próprio menu não conta;
 * - aperta Esc. A escuta é na captura do window e para ali: com um menu aberto o Esc é dele,
 *   e o editor de texto em foco (que sai da edição no Esc) nem fica sabendo.
 *
 * Devolve o "parar de escutar". Usado pelo `raRailMenu`, pelo menu da IA (que fica montado
 * e só se esconde) e pelos menus da barra de formatação.
 */
export function watchMenuDismiss(menu: HTMLElement, close: () => void): () => void {
    /** Último gesto que rola (roda, toque, tecla, arrastar a barra) fora do menu. */
    let lastGesture = 0;

    const onGesture = (event: Event): void => {
        if (event.target instanceof Node && menu.contains(event.target)) return;
        lastGesture = performance.now();
    };

    // Captura no window: pega a rolagem de qualquer ancestral (o painel de itens rola,
    // não a página). Rolagem dentro do próprio menu não conta.
    const onScroll = (event: Event): void => {
        const target = event.target;
        if (target instanceof Node && menu.contains(target)) return;

        // Rolou algo que não leva o menu junto (a prévia do PDF, outro painel).
        const scroller = target instanceof Document ? target.documentElement : target;
        if (!(scroller instanceof Element) || !scroller.contains(menu)) return;

        // Sem gesto recente é rolagem de layout (conteúdo encolheu), não do usuário.
        if (performance.now() - lastGesture > GESTURE_WINDOW_MS) return;

        close();
    };

    const onKeydown = (event: KeyboardEvent): void => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        event.stopPropagation();
        close();
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('keydown', onKeydown, true);
    for (const type of GESTURE_EVENTS)
        window.addEventListener(type, onGesture, { capture: true, passive: true });

    return () => {
        window.removeEventListener('scroll', onScroll, true);
        window.removeEventListener('keydown', onKeydown, true);
        for (const type of GESTURE_EVENTS) window.removeEventListener(type, onGesture, { capture: true });
    };
}
